import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme || 'light';
    });

    const [primaryColor, setPrimaryColor] = useState(() => {
        const savedColor = localStorage.getItem('primaryColor');
        return savedColor || 'default';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Theme Definitions matching Timesheets.js structure
    const themes = {
        default: {
            name: 'Classique',
            primary: '#a4c639',
            secondary: '#8ab82e',
            accent: '#3b82f6',
            success: '#10b981',
            warning: '#f59e0b',
            danger: '#ef4444',
            icon: '🌿'
        },
        ocean: {
            name: 'Océan',
            primary: '#0ea5e9',
            secondary: '#0284c7',
            accent: '#06b6d4',
            success: '#14b8a6',
            warning: '#f59e0b',
            danger: '#ef4444',
            icon: '🌊'
        },
        sunset: {
            name: 'Coucher de Soleil',
            primary: '#f97316',
            secondary: '#ea580c',
            accent: '#fb923c',
            success: '#10b981',
            warning: '#fbbf24',
            danger: '#dc2626',
            icon: '🌅'
        },
        purple: {
            name: 'Violet',
            primary: '#8b5cf6',
            secondary: '#7c3aed',
            accent: '#a78bfa',
            success: '#10b981',
            warning: '#f59e0b',
            danger: '#ef4444',
            icon: '💜'
        },
        forest: {
            name: 'Forêt',
            primary: '#059669',
            secondary: '#047857',
            accent: '#10b981',
            success: '#22c55e',
            warning: '#f59e0b',
            danger: '#ef4444',
            icon: '🌲'
        }
    };

    useEffect(() => {
        localStorage.setItem('primaryColor', primaryColor);

        // Get the full theme object
        const selectedTheme = themes[primaryColor] || themes['default'];

        // Update ALL CSS variables
        document.documentElement.style.setProperty('--color-primary', selectedTheme.primary);
        document.documentElement.style.setProperty('--color-secondary-theme', selectedTheme.secondary); // New name to avoid clash? Or override existing secondary
        document.documentElement.style.setProperty('--color-accent', selectedTheme.accent);

        // Also update derived variables for compatibility
        // Assuming light version is just lighter opacity or specific hex if needed
        document.documentElement.style.setProperty('--color-primary-light', selectedTheme.primary + '20'); // 20 = ~12% opacity hex
        document.documentElement.style.setProperty('--color-primary-dark', selectedTheme.secondary);

    }, [primaryColor]);

    const toggleTheme = () => {
        setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, primaryColor, setPrimaryColor, themes }}>
            {children}
        </ThemeContext.Provider>
    );
};
